import { SHEETS_CONFIG, RANGES, SHEET_NAMES } from '../config/sheets.config';
import { 
  Customer, 
  OnboardingTask, 
  CustomerProgress, 
  HealthScore, 
  OnboardingData,
  PricingSlab,
  TransactionalPricingSlab 
} from '../types';
import { SecureTokenManager } from '../utils/secureTokenManager';
import { ValidationUtils, RateLimiter } from '../utils/validation';

declare global {
  interface Window {
    gapi: any;
    google: any;
  }
}

interface BatchResponse {
  customers: Customer[];
  tasks: OnboardingTask[];
  progress: CustomerProgress[];
  healthScores: HealthScore[];
  pricingSlabs: PricingSlab[];
  transactionalPricingSlabs: TransactionalPricingSlab[];
}

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

class GoogleSheetsService {
  private isInitialized = false;
  private cache = new Map<string, CacheEntry<any>>();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  private pendingRequests = new Map<string, Promise<any>>();
  private rateLimiter = new RateLimiter(100, 60000); // 100 requests per minute

  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    return new Promise((resolve, reject) => {
      
      // Load the Google API client library
      const script = document.createElement('script');
      script.src = 'https://apis.google.com/js/api.js';
      script.onload = () => {
        window.gapi.load('client', async () => {
          try {
            await window.gapi.client.init({
              apiKey: SHEETS_CONFIG.API_KEY,
              discoveryDocs: SHEETS_CONFIG.DISCOVERY_DOCS,
            });
            this.isInitialized = true;
            resolve();
          } catch (error) {
            reject(error);
          }
        });
      };
      script.onerror = (error) => {
        reject(error);
      };
      document.head.appendChild(script);
    });
  }

  setAccessToken(token: string, expiresIn?: number): void {
    const userEmail = SecureTokenManager.getUserEmail();
    const staySignedIn = SecureTokenManager.getStaySignedIn();
    SecureTokenManager.setToken(token, expiresIn, userEmail || undefined, staySignedIn);
    if (window.gapi && window.gapi.client) {
      window.gapi.client.setToken({ access_token: token });
    }
  }

  async signOut(): Promise<void> {
    SecureTokenManager.clearToken();
    this.cache.clear();
    this.pendingRequests.clear();
    if (window.gapi && window.gapi.client) {
      window.gapi.client.setToken(null);
    }
  }

  clearAllCache(): void {
    this.cache.clear();
    this.pendingRequests.clear();
  }

  isSignedIn(): boolean {
    return SecureTokenManager.isTokenValid();
  }

  private async ensureAuthenticated(): Promise<void> {
    await this.rateLimiter.checkRateLimit();
    
    const token = SecureTokenManager.getToken();
    if (!token) {
      throw new Error('No valid authentication token found. Please sign in again.');
    }

    if (window.gapi && window.gapi.client) {
      window.gapi.client.setToken({ access_token: token });
    }
  }

  private async executeWithRetry<T>(
    operation: () => Promise<T>,
    operationName: string,
    retryCount: number = 0
  ): Promise<T> {
    try {
      return await operation();
    } catch (error: any) {
      // Check for 401 authentication error
      if (error?.status === 401 || error?.code === 401 || 
          error?.message?.includes('UNAUTHENTICATED') ||
          error?.message?.includes('invalid authentication credentials')) {
        
        // Only attempt refresh once
        if (retryCount === 0) {
          const userEmail = SecureTokenManager.getUserEmail();
          const staySignedIn = SecureTokenManager.getStaySignedIn();
          
          // If we have user email and stay signed in is enabled, attempt silent refresh
          if (userEmail && staySignedIn && window.google?.accounts?.oauth2) {
            try {
              // Create a promise to handle the token refresh
              const refreshPromise = new Promise<string>((resolve, reject) => {
                const tokenClient = window.google.accounts.oauth2.initTokenClient({
                  client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
                  scope: 'https://www.googleapis.com/auth/spreadsheets https://www.googleapis.com/auth/userinfo.email https://www.googleapis.com/auth/userinfo.profile',
                  hint: userEmail,
                  callback: (tokenResponse: any) => {
                    if (tokenResponse.access_token) {
                      resolve(tokenResponse.access_token);
                    } else {
                      reject(new Error('Failed to refresh token'));
                    }
                  },
                });
                
                // Request token with no prompt
                tokenClient.requestAccessToken({ prompt: '' });
              });
              
              // Wait for token refresh
              const newToken = await refreshPromise;
              
              // Update token
              this.setAccessToken(newToken, 3600);
              
              // Retry the operation
              return await this.executeWithRetry(operation, operationName, retryCount + 1);
            } catch (refreshError) {
              // Silent refresh failed, fall through to reload
            }
          }
        }
        
        // Clear invalid token
        SecureTokenManager.clearToken();
        this.cache.clear();
        
        // Return authentication error to UI layer for graceful handling
        throw new Error('AUTHENTICATION_EXPIRED');
      }
      
      // Re-throw other errors
      throw error;
    }
  }

  private getCacheKey(method: string, ...args: any[]): string {
    return `${method}_${args.join('_')}`;
  }

  private getFromCache<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;
    
    if (Date.now() - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }
    
    return entry.data;
  }

  private setCache<T>(key: string, data: T, ttl: number = this.CACHE_TTL): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl,
    });
  }

  private async withCache<T>(
    key: string,
    fetcher: () => Promise<T>,
    ttl: number = this.CACHE_TTL
  ): Promise<T> {
    // Check cache first
    const cached = this.getFromCache<T>(key);
    if (cached !== null) {
      return cached;
    }

    // Check for pending request
    if (this.pendingRequests.has(key)) {
      return this.pendingRequests.get(key)!;
    }

    // Create new request
    const promise = fetcher().then(data => {
      this.setCache(key, data, ttl);
      this.pendingRequests.delete(key);
      return data;
    }).catch(error => {
      this.pendingRequests.delete(key);
      throw error;
    });

    this.pendingRequests.set(key, promise);
    return promise;
  }

  async batchGetAllData(): Promise<BatchResponse> {
    const cacheKey = 'batch_all_data';
    return this.withCache(cacheKey, async () => {
      await this.ensureAuthenticated();
      
      // Use batch request to get all data in one API call
      const response = await this.executeWithRetry(
        async () => window.gapi.client.sheets.spreadsheets.values.batchGet({
          spreadsheetId: SHEETS_CONFIG.SPREADSHEET_ID,
          ranges: [
            RANGES.CUSTOMERS,
            RANGES.TASKS,
            RANGES.PROGRESS,
            RANGES.HEALTH_SCORES,
            RANGES.PRICING_SLABS,
            RANGES.TRANSACTIONAL_PRICING_SLABS,
          ],
        }),
        'batchGetAllData'
      );

      const [customersData, tasksData, progressData, healthScoresData, pricingSlabsData, transactionalPricingSlabsData] = response.result.valueRanges;

      // Process customers
      const customerRows = customersData.values || [];
      const customers: Customer[] = customerRows.length > 1 
        ? customerRows.slice(1).map((row: any[]) => ({
            id: row[0],
            email: row[1],
            companyName: row[2],
            segment: row[3] as Customer['segment'],
            signupDate: row[4],
            onboardingStatus: row[5] as Customer['onboardingStatus'],
            companyGoal: row[6] || '',
            onboardingCompletedDate: row[7] || undefined,
          }))
        : [];

      // Process tasks
      const taskRows = tasksData.values || [];
      const tasks: OnboardingTask[] = taskRows.length > 1
        ? taskRows.slice(1).map((row: any[]) => ({
            id: row[0],
            title: row[1],
            description: row[2],
            taskType: row[3] as OnboardingTask['taskType'],
            orderIndex: parseInt(row[4]) || 0,
            segment: row[5],
          })).sort((a: OnboardingTask, b: OnboardingTask) => a.orderIndex - b.orderIndex)
        : [];

      // Process progress
      const progressRows = progressData.values || [];
      const progress: CustomerProgress[] = progressRows.length > 1
        ? progressRows.slice(1).map((row: any[]) => ({
            customerId: row[0],
            taskId: row[1],
            status: row[2] as CustomerProgress['status'],
            completedDate: row[3],
            updatedByEmail: row[4] || '',
            updatedByName: row[5] || '',
          }))
        : [];

      // Process health scores
      const healthScoreRows = healthScoresData.values || [];
      const healthScores: HealthScore[] = healthScoreRows.length > 1
        ? healthScoreRows.slice(1).map((row: any[]) => {
            const factors = JSON.parse(row[3] || '{}');
            const parsedScore = parseInt(row[1]);
            return {
              customerId: row[0],
              score: isNaN(parsedScore) ? 0 : parsedScore,
              calculatedDate: row[2],
              factors,
            };
          })
        : [];

      // Process pricing slabs
      const pricingSlabRows = pricingSlabsData.values || [];
      const pricingSlabs: PricingSlab[] = pricingSlabRows.length > 1
        ? pricingSlabRows.slice(1)
            .filter((row: any[]) => row[6] === 'TRUE') // Filter by is_active (column 6)
            .map((row: any[]) => ({
              slabId: row[0],
              currency: row[1] as 'INR' | 'USD',
              minEmails: parseInt(row[2]) || 0,
              maxEmails: row[3] === 'unlimited' ? null : parseInt(row[3]) || null,
              pricePerLakh: parseFloat(row[4]) || 0,
              label: row[5],
              isActive: row[6] === 'TRUE',
              createdDate: row[7],
              updatedDate: row[8],
            }))
            .sort((a: PricingSlab, b: PricingSlab) => a.minEmails - b.minEmails)
        : [];

      // Process transactional pricing slabs
      const transactionalPricingSlabRows = transactionalPricingSlabsData?.values || [];
      const transactionalPricingSlabs: TransactionalPricingSlab[] = transactionalPricingSlabRows.length > 1
        ? transactionalPricingSlabRows.slice(1)
            .filter((row: any[]) => row[6] === 'TRUE') // Filter by is_active (column 6)
            .map((row: any[]) => ({
              slabId: row[0],
              currency: row[1] as 'INR' | 'USD',
              minEmails: parseInt(row[2]) || 0,
              maxEmails: row[3] === 'unlimited' ? null : parseInt(row[3]) || null,
              pricePerThousand: parseFloat(row[4]) || 0,
              label: row[5],
              isActive: row[6] === 'TRUE',
              dedicatedIpPrice: parseFloat(row[7]) || 50,
              createdDate: row[8],
              updatedDate: row[9],
            }))
            .sort((a: TransactionalPricingSlab, b: TransactionalPricingSlab) => a.minEmails - b.minEmails)
        : [];

      return {
        customers,
        tasks,
        progress,
        healthScores,
        pricingSlabs,
        transactionalPricingSlabs,
      };
    });
  }

  async getCustomerData(customerId: string): Promise<Customer | null> {
    try {
      const batchData = await this.batchGetAllData();
      return batchData.customers.find(c => c.id === customerId) || null;
    } catch (error) {
      throw error;
    }
  }

  async getAllCustomers(): Promise<Customer[]> {
    try {
      const batchData = await this.batchGetAllData();
      return batchData.customers;
    } catch (error) {
      throw error;
    }
  }

  async getOnboardingTasks(segment?: string): Promise<OnboardingTask[]> {
    try {
      const batchData = await this.batchGetAllData();
      return segment 
        ? batchData.tasks.filter(task => task.segment === 'all' || task.segment === segment)
        : batchData.tasks;
    } catch (error) {
      throw error;
    }
  }

  async getCustomerProgress(customerId: string): Promise<CustomerProgress[]> {
    try {
      const batchData = await this.batchGetAllData();
      return batchData.progress.filter(p => p.customerId === customerId);
    } catch (error) {
      throw error;
    }
  }

  async updateTaskProgress(
    customerId: string, 
    taskId: string, 
    status: CustomerProgress['status'],
    userInfo?: { email: string; name: string }
  ): Promise<void> {
    // Validate inputs
    if (!ValidationUtils.isValidCustomerId(customerId)) {
      throw new Error('Invalid customer ID');
    }
    if (!ValidationUtils.isValidTaskId(taskId)) {
      throw new Error('Invalid task ID');
    }
    if (!ValidationUtils.isValidTaskStatus(status)) {
      throw new Error('Invalid task status');
    }

    try {
      await this.ensureAuthenticated();
      
      // First, find if this progress record exists
      const progressData = await this.getCustomerProgress(customerId);
      const existingProgress = progressData.find(p => p.taskId === taskId);
      
      const completedDate = status === 'completed' ? new Date().toISOString() : '';
      const updatedByEmail = userInfo?.email || 'system@mailercloud.com';
      const updatedByName = userInfo?.name || 'System';
      const values = [[customerId, taskId, status, completedDate, updatedByEmail, updatedByName]];

      if (existingProgress) {
        // Update existing row - need fresh data for row index
        const response = await this.executeWithRetry(
          async () => window.gapi.client.sheets.spreadsheets.values.get({
            spreadsheetId: SHEETS_CONFIG.SPREADSHEET_ID,
            range: RANGES.PROGRESS,
          }),
          'updateTaskProgress-get'
        );

        const rows = response.result.values || [];
        const rowIndex = rows.findIndex(
          (row: any[]) => row[0] === customerId && row[1] === taskId
        );

        if (rowIndex > 0) {
          await this.executeWithRetry(
            async () => window.gapi.client.sheets.spreadsheets.values.update({
              spreadsheetId: SHEETS_CONFIG.SPREADSHEET_ID,
              range: `${SHEET_NAMES.PROGRESS}!A${rowIndex + 1}:F${rowIndex + 1}`,
              valueInputOption: 'RAW',
              resource: { values },
            }),
            'updateTaskProgress-update'
          );
        }
      } else {
        // Append new row
        await this.executeWithRetry(
          async () => window.gapi.client.sheets.spreadsheets.values.append({
            spreadsheetId: SHEETS_CONFIG.SPREADSHEET_ID,
            range: RANGES.PROGRESS,
            valueInputOption: 'RAW',
            insertDataOption: 'INSERT_ROWS',
            resource: { values },
          }),
          'updateTaskProgress-append'
        );
      }

      // Update onboarding status based on task progress FIRST
      await this.updateOnboardingStatusBasedOnProgress(customerId);
      
      // Recalculate health score with fresh data
      try {
        await this.calculateAndSaveHealthScore(customerId);
      } catch (healthScoreError) {
        console.warn('Failed to update health score:', healthScoreError);
      }
      
      // THEN clear cache entries to ensure consistency
      this.cache.delete(`getOnboardingData_${customerId}_false`);
      this.cache.delete(`getOnboardingData_${customerId}_true`);
      this.cache.delete(`getHealthScore_${customerId}`);
      this.cache.delete('batch_all_data'); // Clear batch cache to ensure fresh data
      
    } catch (error) {
      throw error;
    }
  }

  async validateAndFixCustomerStatus(customerId: string): Promise<{ statusCorrected: boolean; oldStatus?: string; newStatus?: string }> {
    try {
      // Clear cache to ensure fresh data for validation
      this.cache.delete('batch_all_data');
      
      // Get fresh data for validation
      const [customer, tasks, progress] = await Promise.all([
        this.getCustomerData(customerId),
        this.getOnboardingTasks(),
        this.getCustomerProgress(customerId),
      ]);
      
      if (!customer) {
        return { statusCorrected: false };
      }
      
      // Calculate expected metrics using the same logic as dashboard
      const customerTasks = tasks.filter(t => t.segment === 'all' || t.segment === customer.segment);
      const requiredTasks = customerTasks.filter(t => t.taskType === 'required');
      const completedRequired = progress.filter(p => 
        p.status === 'completed' && 
        requiredTasks.some(t => String(t.id) === String(p.taskId))
      );
      const completedTasks = progress.filter(p => 
        p.status === 'completed' && 
        customerTasks.some(t => String(t.id) === String(p.taskId))
      );
      
      const expectedProgress = requiredTasks.length > 0 
        ? (completedRequired.length / requiredTasks.length) * 100 
        : 0;
      
      // Use the same logic as updateOnboardingStatusBasedOnProgress
      let expectedStatus: Customer['onboardingStatus'] = 'not_started';
      if (completedRequired.length === requiredTasks.length && requiredTasks.length > 0) {
        expectedStatus = 'completed';
      } else if (completedTasks.length > 0) {
        expectedStatus = 'in_progress';
      }
      
      // Validate and fix status if inconsistent
      if (customer.onboardingStatus !== expectedStatus) {
        console.warn(`Status inconsistency detected for ${customer.companyName} (${customerId}): ` +
          `current=${customer.onboardingStatus}, expected=${expectedStatus}, progress=${expectedProgress}%`);
        
        // Fix the status using the existing method
        await this.updateOnboardingStatusBasedOnProgress(customerId);
        
        return { 
          statusCorrected: true, 
          oldStatus: customer.onboardingStatus, 
          newStatus: expectedStatus 
        };
      }
      
      // Validate completion date for completed customers
      if (customer.onboardingStatus === 'completed' && !customer.onboardingCompletedDate) {
        console.info(`Migrating completion date for completed customer ${customerId}...`);
        
        // Get customer progress to find latest completion date
        const completedTasks = progress
          .filter(p => p.status === 'completed' && p.completedDate)
          .sort((a, b) => new Date(b.completedDate!).getTime() - new Date(a.completedDate!).getTime());
        
        let estimatedCompletionDate: string;
        if (completedTasks.length > 0) {
          estimatedCompletionDate = completedTasks[0].completedDate!;
        } else {
          // Fallback: estimate 30 days from signup
          const signupDate = new Date(customer.signupDate);
          estimatedCompletionDate = new Date(signupDate.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString();
        }
        
        await this.updateCustomer(customerId, {
          onboardingCompletedDate: estimatedCompletionDate
        });
      }
      
      // Validate health score exists and is not stale
      const healthScore = await this.getHealthScore(customerId);
      if (!healthScore || 
          (Date.now() - new Date(healthScore.calculatedDate).getTime()) > 60 * 60 * 1000) {
        console.info(`Recalculating health score for ${customerId}...`);
        await this.calculateAndSaveHealthScore(customerId);
      }
      
      return { statusCorrected: false };
    } catch (error) {
      console.error(`Status validation failed for ${customerId}:`, error);
      return { statusCorrected: false };
    }
  }

  async validateAllCustomerStatuses(): Promise<{ totalChecked: number; totalCorrected: number; corrections: any[] }> {
    try {
      console.log('Starting batch status validation for all customers...');
      
      const customers = await this.getAllCustomers();
      let corrected = 0;
      const corrections: any[] = [];
      
      for (const customer of customers) {
        const result = await this.validateAndFixCustomerStatus(customer.id);
        if (result.statusCorrected) {
          corrected++;
          corrections.push({
            customerId: customer.id,
            companyName: customer.companyName,
            oldStatus: result.oldStatus,
            newStatus: result.newStatus
          });
        }
      }
      
      console.log(`Status validation complete: ${corrected}/${customers.length} customers corrected`);
      return { 
        totalChecked: customers.length, 
        totalCorrected: corrected, 
        corrections 
      };
    } catch (error) {
      console.error('Batch status validation failed:', error);
      throw error;
    }
  }

  private async updateOnboardingStatusBasedOnProgress(customerId: string): Promise<void> {
    try {
      // Get customer data first to know the segment
      const customer = await this.getCustomerData(customerId);
      
      if (!customer) {
        return;
      }

      // Get all tasks and progress
      const [allTasks, progress] = await Promise.all([
        this.getOnboardingTasks(), // Get ALL tasks, then filter properly
        this.getCustomerProgress(customerId),
      ]);

      // Filter tasks for this customer's segment (all + customer's specific segment)
      const customerTasks = allTasks.filter(t => t.segment === 'all' || t.segment === customer.segment);
      const requiredTasks = customerTasks.filter(t => t.taskType === 'required');

      // Check which required tasks are completed (ensure string comparison)
      const completedRequired = progress.filter(p => {
        return p.status === 'completed' && 
               requiredTasks.some(t => String(t.id) === String(p.taskId));
      });

      // Count ANY completed tasks for in_progress status
      const completedTasks = progress.filter(p => {
        return p.status === 'completed' &&
               customerTasks.some(t => String(t.id) === String(p.taskId));
      });

      // Determine new onboarding status with clear rules
      let newStatus: Customer['onboardingStatus'] = 'not_started';

      if (completedRequired.length === requiredTasks.length && requiredTasks.length > 0) {
        // All required tasks for this segment completed → status = completed
        newStatus = 'completed';
      } else if (completedTasks.length > 0) {
        // At least one task completed → status = in_progress
        newStatus = 'in_progress';
      }
      // else: No tasks completed → status remains not_started

      // Only update if status has changed
      if (customer.onboardingStatus !== newStatus) {
        await this.updateCustomerStatus(customerId, newStatus);
      }
    } catch (error) {
      console.error(`Failed to update onboarding status for ${customerId}:`, error);
      // Don't throw error to avoid breaking the main task update
    }
  }

  async updateCustomerStatus(
    customerId: string, 
    status: Customer['onboardingStatus']
  ): Promise<void> {
    // Validate inputs
    if (!ValidationUtils.isValidCustomerId(customerId)) {
      throw new Error('Invalid customer ID');
    }
    if (!['not_started', 'in_progress', 'completed'].includes(status)) {
      throw new Error('Invalid onboarding status');
    }

    try {
      await this.ensureAuthenticated();
      
      // Get current customer data to find row index
      const response = await this.executeWithRetry(
        async () => window.gapi.client.sheets.spreadsheets.values.get({
          spreadsheetId: SHEETS_CONFIG.SPREADSHEET_ID,
          range: RANGES.CUSTOMERS,
        }),
        'updateCustomerStatus-get'
      );

      const rows = response.result.values || [];
      const rowIndex = rows.findIndex(
        (row: any[]) => row[0] === customerId
      );

      if (rowIndex > 0) {
        // Get current row data to preserve existing values
        const currentRow = rows[rowIndex];
        const completionDate = status === 'completed' ? new Date().toISOString() : (currentRow[7] || '');
        
        // Update both onboarding_status (column F, index 5) and completion date (column H, index 7)
        const updatedValues = [
          [status, currentRow[6] || '', completionDate] // status, company_goal, completion_date
        ];
        
        await this.executeWithRetry(
          async () => window.gapi.client.sheets.spreadsheets.values.update({
            spreadsheetId: SHEETS_CONFIG.SPREADSHEET_ID,
            range: `${SHEET_NAMES.CUSTOMERS}!F${rowIndex + 1}:H${rowIndex + 1}`,
            valueInputOption: 'RAW',
            resource: { values: updatedValues },
          }),
          'updateCustomerStatus-update'
        );

        // Automatically recalculate and save health score FIRST
        try {
          await this.calculateAndSaveHealthScore(customerId);
        } catch (healthScoreError) {
          // Don't fail the entire operation if health score calculation fails
          console.warn(`Health score calculation failed for customer ${customerId}:`, healthScoreError);
        }

        // THEN invalidate cache to ensure consistency
        const cacheKeys = [
          'batch_all_data',
          this.getCacheKey('getCustomerData', customerId),
          this.getCacheKey('getAllCustomers'),
          this.getCacheKey('getHealthScore', customerId),
          this.getCacheKey('getOnboardingData', customerId)
        ];
        cacheKeys.forEach(key => this.cache.delete(key));
      } else {
        throw new Error('Customer not found');
      }
    } catch (error) {
      throw error;
    }
  }

  async getHealthScore(customerId: string): Promise<HealthScore | null> {
    try {
      const batchData = await this.batchGetAllData();
      const customerScores = batchData.healthScores
        .filter(score => score.customerId === customerId)
        .sort((a, b) => new Date(b.calculatedDate).getTime() - new Date(a.calculatedDate).getTime());

      return customerScores.length > 0 ? customerScores[0] : null;
    } catch (error) {
      throw error;
    }
  }

  async calculateAndSaveHealthScore(customerId: string): Promise<HealthScore> {
    try {
      await this.ensureAuthenticated();
      const [customer, tasks, progress] = await Promise.all([
        this.getCustomerData(customerId),
        this.getOnboardingTasks(),
        this.getCustomerProgress(customerId),
      ]);

      if (!customer) throw new Error('Customer not found');

      // Calculate progress score - Only count completed REQUIRED tasks
      const requiredTasks = tasks.filter(t => t.taskType === 'required');
      const completedRequiredTasks = progress.filter(p => 
        p.status === 'completed' && 
        requiredTasks.some(t => t.id === p.taskId)
      ).length;
      const progressScore = requiredTasks.length > 0 ? (completedRequiredTasks / requiredTasks.length) * 100 : 0;

      // Calculate time score - Use actual completion time for completed customers
      const signupDate = customer.signupDate ? new Date(customer.signupDate) : new Date();
      const isFullyCompleted = progressScore === 100;
      
      let daysInOnboarding;
      if (isFullyCompleted && customer.onboardingCompletedDate) {
        // For completed customers, use actual completion time
        daysInOnboarding = Math.floor(
          (new Date(customer.onboardingCompletedDate).getTime() - signupDate.getTime()) / (1000 * 60 * 60 * 24)
        );
      } else {
        // For in-progress or customers without completion date, use current time
        daysInOnboarding = Math.floor(
          (new Date().getTime() - signupDate.getTime()) / (1000 * 60 * 60 * 24)
        );
      }
      
      let timeScore;
      
      if (isFullyCompleted) {
        // Completed customers get better time scores
        if (daysInOnboarding <= 14) {
          timeScore = 100; // Bonus for quick completion
        } else if (daysInOnboarding <= 30) {
          timeScore = 90; // Very good
        } else if (daysInOnboarding <= 60) {
          timeScore = 75; // Good
        } else {
          timeScore = 60; // Minimum for completed customers
        }
      } else {
        // In-progress customers: 1 point penalty per day, minimum 20
        timeScore = Math.max(20, 100 - daysInOnboarding);
      }

      // Calculate engagement score - Improved logic for completed customers
      let engagementScore;
      
      if (isFullyCompleted) {
        // Completed customers get high engagement score
        engagementScore = 90;
      } else {
        // In-progress customers: Based on recent activity and overall progress
        const recentProgress = progress.filter(p => {
          if (!p.completedDate) return false;
          try {
            const daysSinceCompletion = Math.floor(
              (new Date().getTime() - new Date(p.completedDate).getTime()) / (1000 * 60 * 60 * 24)
            );
            return daysSinceCompletion <= 14; // Extended to 14 days
          } catch {
            return false;
          }
        }).length;
        
        // Base engagement on recent activity + overall progress momentum
        const recentActivityScore = Math.min(60, recentProgress * 20);
        const momentumScore = Math.min(40, progressScore * 0.4);
        engagementScore = Math.round(recentActivityScore + momentumScore);
      }

      // Calculate overall score with rebalanced weights and completion bonus
      const baseScore = (progressScore * 0.6) + (timeScore * 0.25) + (engagementScore * 0.15);
      
      // Add completion bonus for 100% required task completion
      const completionBonus = isFullyCompleted ? 10 : 0;
      
      const rawScore = baseScore + completionBonus;
      const score = Math.round(isNaN(rawScore) ? 0 : Math.max(0, Math.min(100, rawScore)));

      const healthScore: HealthScore = {
        customerId,
        score,
        calculatedDate: new Date().toISOString(),
        factors: {
          progress: Math.round(isNaN(progressScore) ? 0 : progressScore),
          engagement: Math.round(isNaN(engagementScore) ? 0 : engagementScore),
          timeInOnboarding: Math.round(isNaN(timeScore) ? 0 : timeScore),
        },
      };

      // Save to sheet
      const values = [[
        customerId,
        score.toString(),
        healthScore.calculatedDate,
        JSON.stringify(healthScore.factors),
      ]];

      await this.executeWithRetry(
        async () => gapi.client.sheets.spreadsheets.values.append({
          spreadsheetId: SHEETS_CONFIG.SPREADSHEET_ID,
          range: RANGES.HEALTH_SCORES,
          valueInputOption: 'RAW',
          insertDataOption: 'INSERT_ROWS',
          resource: { values },
        }),
        'calculateAndSaveHealthScore-append'
      );

      return healthScore;
    } catch (error) {
      throw error;
    }
  }

  async getOnboardingData(customerId: string, forceRefresh: boolean = false): Promise<OnboardingData | null> {
    try {
      await this.ensureAuthenticated();
      
      // If force refresh requested, clear cache for customer data first
      if (forceRefresh) {
        const customerCacheKeys = [
          'batch_all_data',
          this.getCacheKey('getCustomerData', customerId),
        ];
        customerCacheKeys.forEach(key => this.cache.delete(key));
      }
      
      const [customer, tasks, progress, healthScore] = await Promise.all([
        this.getCustomerData(customerId),
        this.getOnboardingTasks(),
        this.getCustomerProgress(customerId),
        this.getHealthScore(customerId),
      ]);

      if (!customer) return null;

      // Check if health score needs to be recalculated
      let score = healthScore;
      
      if (!score) {
        // No health score exists, calculate one
        score = await this.calculateAndSaveHealthScore(customerId);
      } else {
        // Check if health score is stale (older than 1 hour)
        const scoreAge = Date.now() - new Date(score.calculatedDate).getTime();
        const oneHourInMs = 60 * 60 * 1000;
        
        if (scoreAge > oneHourInMs) {
          // Recalculate stale health score
          score = await this.calculateAndSaveHealthScore(customerId);
        }
      }

      return {
        customer,
        tasks: tasks.filter(t => t.segment === 'all' || t.segment === customer.segment),
        progress,
        healthScore: score,
      };
    } catch (error) {
      throw error;
    }
  }

  async createSampleData(): Promise<void> {
    try {
      await this.ensureAuthenticated();
      // Create sample customers - all start with not_started status
      const customers = [
        ['1', 'john@acme.com', 'Acme Corp', 'small_business', '2025-01-15', 'not_started', 'Increase email marketing ROI by 40% through automated workflows and personalized campaigns targeting our 5,000+ B2B prospects within the next 6 months.', ''],
        ['2', 'sarah@techco.com', 'TechCo', 'enterprise', '2025-01-20', 'not_started', 'Scale our email infrastructure to handle 100K+ daily emails while maintaining 99.9% deliverability and implementing advanced segmentation for our global customer base.', ''],
        ['3', 'mike@shop.com', 'Shop Inc', 'e-commerce', '2025-01-18', 'not_started', 'Recover 25% of abandoned carts through triggered email sequences and boost customer lifetime value by implementing behavioral-based email automation.', ''],
      ];

      // Create sample tasks
      const tasks = [
        ['1', 'Connect Your Domain', 'Add and verify your sending domain', 'required', '1', 'all'],
        ['2', 'Import Contacts', 'Upload your email list or connect your CRM', 'required', '2', 'all'],
        ['3', 'Create First Campaign', 'Design and send your first email campaign', 'required', '3', 'all'],
        ['4', 'Set Up Automation', 'Create your first automated workflow', 'recommended', '4', 'all'],
        ['5', 'Configure SPF/DKIM', 'Improve email deliverability', 'recommended', '5', 'all'],
        ['6', 'Create Signup Forms', 'Build forms to grow your list', 'optional', '6', 'all'],
      ];

      // Create minimal sample progress - let automatic status updates handle progression
      const progress: string[][] = [
        // Customer 1 has no progress yet (will stay not_started)
        // Customer 2 has no progress yet (will stay not_started)  
        // Customer 3 has no progress yet (will stay not_started)
        // Progress will be created when users interact with tasks
      ];

      // Only create data if sheets are empty
      const response = await this.executeWithRetry(
        async () => window.gapi.client.sheets.spreadsheets.values.get({
          spreadsheetId: SHEETS_CONFIG.SPREADSHEET_ID,
          range: RANGES.CUSTOMERS,
        }),
        'createSampleData-get'
      );

      if (!response.result.values || response.result.values.length <= 1) {
        // Add headers and data
        const batchData = [
          {
            range: 'Customers!A1:H1',
            values: [['customer_id', 'email', 'company_name', 'segment', 'signup_date', 'onboarding_status', 'company_goal', 'onboarding_completed_date']],
          },
          {
            range: 'Customers!A2:H4',
            values: customers,
          },
          {
            range: 'Onboarding_Tasks!A1:F1',
            values: [['task_id', 'title', 'description', 'task_type', 'order_index', 'segment']],
          },
          {
            range: 'Onboarding_Tasks!A2:F7',
            values: tasks,
          },
          {
            range: 'Customer_Progress!A1:F1',
            values: [['customer_id', 'task_id', 'status', 'completed_date', 'updated_by_email', 'updated_by_name']],
          },
          {
            range: 'Health_Scores!A1:D1',
            values: [['customer_id', 'score', 'calculated_date', 'factors']],
          },
        ];

        // Only add progress data if there are progress entries
        if (progress.length > 0) {
          batchData.splice(-1, 0, {
            range: 'Customer_Progress!A2:F' + (1 + progress.length),
            values: progress,
          });
        }

        await this.executeWithRetry(
          async () => window.gapi.client.sheets.spreadsheets.values.batchUpdate({
            spreadsheetId: SHEETS_CONFIG.SPREADSHEET_ID,
            resource: {
              valueInputOption: 'RAW',
              data: batchData,
            },
          }),
          'createSampleData-batchUpdate'
        );
      }
    } catch (error) {
      throw error;
    }
  }

  async addCustomer(customer: Customer): Promise<void> {
    try {
      await this.ensureAuthenticated();

      // Validate customer data
      if (!customer.id || !customer.email || !customer.companyName || !customer.segment) {
        throw new Error('Missing required customer fields');
      }

      // Company goal can contain HTML formatting, so we don't sanitize it but validate it
      const companyGoal = customer.companyGoal || '';

      // Prepare row data in the order: customer_id, email, company_name, segment, signup_date, onboarding_status, company_goal, onboarding_completed_date
      const rowData = [
        customer.id,
        customer.email,
        customer.companyName,
        customer.segment,
        customer.signupDate,
        customer.onboardingStatus,
        companyGoal,
        customer.onboardingCompletedDate || '',
      ];

      // Append the new customer to the Customers sheet
      await this.executeWithRetry(
        async () => window.gapi.client.sheets.spreadsheets.values.append({
          spreadsheetId: SHEETS_CONFIG.SPREADSHEET_ID,
          range: RANGES.CUSTOMERS,
          valueInputOption: 'RAW',
          insertDataOption: 'INSERT_ROWS',
          resource: { 
            values: [rowData] 
          },
        }),
        'addCustomer-append'
      );

      // Clear relevant caches to ensure fresh data
      this.cache.delete('batchGetAllData');
      this.cache.delete('getAllCustomers');
      this.cache.delete(`customerData-${customer.id}`);

    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`Failed to add customer: ${error.message}`);
      }
      throw new Error('Failed to add customer due to an unknown error');
    }
  }

  async updateCustomer(customerId: string, updatedFields: Partial<Customer>): Promise<void> {
    // Validate inputs
    if (!ValidationUtils.isValidCustomerId(customerId)) {
      throw new Error('Invalid customer ID');
    }

    try {
      await this.ensureAuthenticated();
      
      // Invalidate cache after update
      const cacheKeys = [
        'batch_all_data',
        this.getCacheKey('getCustomerData', customerId),
        this.getCacheKey('getAllCustomers'),
        this.getCacheKey('getHealthScore', customerId),
        this.getCacheKey('getOnboardingData', customerId)
      ];
      cacheKeys.forEach(key => this.cache.delete(key));
      
      // Get current customer data to find row index
      const response = await this.executeWithRetry(
        async () => window.gapi.client.sheets.spreadsheets.values.get({
          spreadsheetId: SHEETS_CONFIG.SPREADSHEET_ID,
          range: RANGES.CUSTOMERS,
        }),
        'updateCustomer-get'
      );

      const rows = response.result.values || [];
      const rowIndex = rows.findIndex(
        (row: any[]) => row[0] === customerId
      );

      if (rowIndex > 0) {
        // Get current row data
        const currentRow = rows[rowIndex];
        
        // Create updated row with only changed fields
        // Schema: customer_id, email, company_name, segment, signup_date, onboarding_status, company_goal, onboarding_completed_date
        const updatedRow = [
          currentRow[0], // customer_id (never changes)
          updatedFields.email !== undefined ? updatedFields.email : currentRow[1],
          updatedFields.companyName !== undefined ? updatedFields.companyName : currentRow[2],
          updatedFields.segment !== undefined ? updatedFields.segment : currentRow[3],
          updatedFields.signupDate !== undefined ? updatedFields.signupDate : currentRow[4],
          updatedFields.onboardingStatus !== undefined ? updatedFields.onboardingStatus : currentRow[5],
          updatedFields.companyGoal !== undefined ? updatedFields.companyGoal : (currentRow[6] || ''),
          updatedFields.onboardingCompletedDate !== undefined ? updatedFields.onboardingCompletedDate : (currentRow[7] || ''),
        ];

        // Update the entire row
        await this.executeWithRetry(
          async () => window.gapi.client.sheets.spreadsheets.values.update({
            spreadsheetId: SHEETS_CONFIG.SPREADSHEET_ID,
            range: `${SHEET_NAMES.CUSTOMERS}!A${rowIndex + 1}:H${rowIndex + 1}`,
            valueInputOption: 'RAW',
            resource: { values: [updatedRow] },
          }),
          'updateCustomer-update'
        );

        // If segment changed, might need to recalculate health score
        if (updatedFields.segment !== undefined) {
          try {
            await this.calculateAndSaveHealthScore(customerId);
          } catch (healthScoreError) {
            // Don't fail the entire operation if health score calculation fails
          }
        }
      } else {
        throw new Error('Customer not found');
      }
    } catch (error) {
      throw error;
    }
  }

  async getPricingSlabs(currency?: 'INR' | 'USD'): Promise<PricingSlab[]> {
    try {
      const batchData = await this.batchGetAllData();
      const slabs = batchData.pricingSlabs;
      
      if (currency) {
        return slabs.filter(slab => slab.currency === currency);
      }
      
      return slabs;
    } catch (error) {
      throw error;
    }
  }

  async getPricingSlabsByCurrency(currency: 'INR' | 'USD'): Promise<PricingSlab[]> {
    return this.getPricingSlabs(currency);
  }

  async getTransactionalPricingSlabs(currency?: 'INR' | 'USD'): Promise<TransactionalPricingSlab[]> {
    try {
      const batchData = await this.batchGetAllData();
      const slabs = batchData.transactionalPricingSlabs;
      
      // If no slabs found, try to initialize the data
      if (!slabs || slabs.length === 0) {
        console.log('No transactional pricing slabs found, initializing...');
        try {
          await this.initializeTransactionalPricingData();
          // Clear cache to force re-fetch
          this.clearAllCache();
          // Retry fetching data
          const retryData = await this.batchGetAllData();
          const retrySlabs = retryData.transactionalPricingSlabs;
          
          if (currency) {
            return retrySlabs.filter(slab => slab.currency === currency);
          }
          return retrySlabs;
        } catch (initError) {
          console.error('Failed to initialize transactional pricing data:', initError);
          // Return empty array instead of throwing
          return [];
        }
      }
      
      if (currency) {
        return slabs.filter(slab => slab.currency === currency);
      }
      
      return slabs;
    } catch (error) {
      console.error('Error fetching transactional pricing slabs:', error);
      // Return empty array instead of throwing
      return [];
    }
  }

  async getTransactionalPricingSlabsByCurrency(currency: 'INR' | 'USD'): Promise<TransactionalPricingSlab[]> {
    return this.getTransactionalPricingSlabs(currency);
  }

  async initializeTransactionalPricingData(): Promise<void> {
    try {
      await this.ensureAuthenticated();

      // First, check if the sheet exists by trying to get spreadsheet metadata
      let sheetExists = false;
      try {
        const spreadsheet = await this.executeWithRetry(
          async () => window.gapi.client.sheets.spreadsheets.get({
            spreadsheetId: SHEETS_CONFIG.SPREADSHEET_ID,
          }),
          'initializeTransactionalPricing-checkSheet'
        );
        
        // Check if our sheet exists
        sheetExists = spreadsheet.result.sheets.some(
          (sheet: any) => sheet.properties.title === SHEET_NAMES.TRANSACTIONAL_PRICING_SLABS
        );
      } catch (error) {
        console.error('Error checking sheet existence:', error);
      }

      // If sheet doesn't exist, create it
      if (!sheetExists) {
        try {
          await this.executeWithRetry(
            async () => window.gapi.client.sheets.spreadsheets.batchUpdate({
              spreadsheetId: SHEETS_CONFIG.SPREADSHEET_ID,
              resource: {
                requests: [{
                  addSheet: {
                    properties: {
                      title: SHEET_NAMES.TRANSACTIONAL_PRICING_SLABS,
                      gridProperties: {
                        rowCount: 100,
                        columnCount: 10
                      }
                    }
                  }
                }]
              }
            }),
            'initializeTransactionalPricing-createSheet'
          );
          console.log('Created Transactional_Pricing_Slabs sheet');
        } catch (error) {
          console.error('Error creating sheet:', error);
          throw error;
        }
      }

      // Now check if sheet has data
      const response = await this.executeWithRetry(
        async () => window.gapi.client.sheets.spreadsheets.values.get({
          spreadsheetId: SHEETS_CONFIG.SPREADSHEET_ID,
          range: RANGES.TRANSACTIONAL_PRICING_SLABS,
        }),
        'initializeTransactionalPricing-get'
      ).catch(() => ({ result: { values: [] } })); // If error, assume empty

      if (!response.result.values || response.result.values.length <= 1) {
        // Add headers and default data
        const now = new Date().toISOString();
        const batchData = [
          {
            range: 'Transactional_Pricing_Slabs!A1:J1',
            values: [['slab_id', 'currency', 'min_emails', 'max_emails', 'price_per_thousand', 'label', 'is_active', 'dedicated_ip_price', 'created_date', 'updated_date']],
          },
          {
            range: 'Transactional_Pricing_Slabs!A2:J5',
            values: [
              // USD pricing - using numbers instead of strings for numeric values
              ['trans_usd_standard', 'USD', 0, 1500000, 0.20, 'Standard (up to 1.5M)', 'TRUE', 50, now, now],
              ['trans_usd_high_volume', 'USD', 1500001, 'unlimited', 0.18, 'High Volume (above 1.5M)', 'TRUE', 50, now, now],
              // INR pricing (assuming 1 USD = 83 INR)
              ['trans_inr_standard', 'INR', 0, 1500000, 16.60, 'Standard (up to 1.5M)', 'TRUE', 4150, now, now],
              ['trans_inr_high_volume', 'INR', 1500001, 'unlimited', 14.94, 'High Volume (above 1.5M)', 'TRUE', 4150, now, now],
            ],
          },
        ];

        await this.executeWithRetry(
          async () => window.gapi.client.sheets.spreadsheets.values.batchUpdate({
            spreadsheetId: SHEETS_CONFIG.SPREADSHEET_ID,
            resource: {
              valueInputOption: 'USER_ENTERED', // Changed from RAW to properly handle numbers
              data: batchData,
            },
          }),
          'initializeTransactionalPricing-batchUpdate'
        );
        
        console.log('Initialized Transactional Pricing data');
      }
    } catch (error) {
      console.error('Error initializing transactional pricing data:', error);
      throw error;
    }
  }

  async recalculateAllCustomerStatuses(): Promise<void> {
    try {
      await this.ensureAuthenticated();
      
      // Clear all cache first
      this.clearAllCache();
      
      // Get all customers and their progress
      const allCustomers = await this.getAllCustomers();
      
      console.log(`Recalculating status for ${allCustomers.length} customers...`);
      
      // Process each customer
      for (const customer of allCustomers) {
        try {
          // Trigger status update for each customer
          await this.updateOnboardingStatusBasedOnProgress(customer.id);
          console.log(`Updated status for customer ${customer.id} (${customer.companyName})`);
        } catch (error) {
          console.error(`Failed to update status for customer ${customer.id}:`, error);
        }
      }
      
      // Clear cache again to ensure fresh data
      this.clearAllCache();
      
      console.log('Completed recalculating all customer statuses');
    } catch (error) {
      console.error('Failed to recalculate customer statuses:', error);
      throw error;
    }
  }

  async migrateCompletedCustomerDates(): Promise<{ totalChecked: number; totalMigrated: number; migrations: any[] }> {
    try {
      console.log('Starting completion date migration for completed customers...');
      
      // Get all customers
      const customers = await this.getAllCustomers();
      const completedCustomers = customers.filter(c => 
        c.onboardingStatus === 'completed' && !c.onboardingCompletedDate
      );
      
      if (completedCustomers.length === 0) {
        console.log('No customers need completion date migration');
        return { totalChecked: customers.length, totalMigrated: 0, migrations: [] };
      }
      
      console.log(`Found ${completedCustomers.length} completed customers without completion dates`);
      
      let migrated = 0;
      const migrations: any[] = [];
      
      for (const customer of completedCustomers) {
        try {
          // Get customer progress to find latest completion date
          const progress = await this.getCustomerProgress(customer.id);
          const completedTasks = progress
            .filter(p => p.status === 'completed' && p.completedDate)
            .sort((a, b) => new Date(b.completedDate!).getTime() - new Date(a.completedDate!).getTime());
          
          let estimatedCompletionDate: string;
          let migrationReason: string;
          
          if (completedTasks.length > 0) {
            // Use the latest task completion date
            estimatedCompletionDate = completedTasks[0].completedDate!;
            migrationReason = 'latest_task_completion';
          } else {
            // Fallback: estimate 30 days from signup
            const signupDate = new Date(customer.signupDate);
            const estimatedDate = new Date(signupDate.getTime() + 30 * 24 * 60 * 60 * 1000);
            estimatedCompletionDate = estimatedDate.toISOString();
            migrationReason = 'signup_plus_30_days';
          }
          
          // Update customer with completion date
          await this.updateCustomer(customer.id, {
            onboardingCompletedDate: estimatedCompletionDate
          });
          
          migrated++;
          migrations.push({
            customerId: customer.id,
            companyName: customer.companyName,
            estimatedCompletionDate,
            migrationReason
          });
          
          console.log(`Migrated completion date for ${customer.companyName}: ${estimatedCompletionDate} (${migrationReason})`);
          
        } catch (error) {
          console.error(`Failed to migrate completion date for customer ${customer.id}:`, error);
        }
      }
      
      console.log(`Completion date migration complete: ${migrated}/${completedCustomers.length} customers migrated`);
      return { 
        totalChecked: customers.length, 
        totalMigrated: migrated, 
        migrations 
      };
    } catch (error) {
      console.error('Completion date migration failed:', error);
      throw error;
    }
  }
}

const googleSheetsService = new GoogleSheetsService();
export default googleSheetsService;